import React, { useCallback, useEffect, useState } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import {
  messageFromApiError,
  messageFromApiResponse,
} from "../../utils/apiMessages";
import { hasDbEditorUpdateAccessFromStorage } from "../../utils/authAccess";
import "./addNewModelModal.css";

const EMPTY_FORM = {
  modelNumber: "",
  modelName: "",
  upc: "",
  imageUrl: "",
  brandId: "",
  taxonomyId: "",
  hasPart: false,
};

/**
 * Shared Add New Model dialog (PUT /model/addNewModel).
 */
export default function AddNewModelModal({ open, onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [brands, setBrands] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [taxonomiesLoading, setTaxonomiesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isAdmin = hasDbEditorUpdateAccessFromStorage();
  const username = localStorage.getItem("username") || "DBEditorWeb";

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setError("");
  }, []);

  const fetchBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const brandsData = await fetchWithAuth({
        method: "GET",
        endpoint: "/brands/getAllActiveBrands",
      });
      const list = Array.isArray(brandsData) ? brandsData : [];
      setBrands(list);
      return list;
    } catch (err) {
      console.error("Error fetching brands:", err);
      setBrands([]);
      setError("Failed to load brands.");
      return [];
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  const fetchTaxonomies = useCallback(async () => {
    setTaxonomiesLoading(true);
    try {
      const taxonomyData = await fetchWithAuth({
        method: "GET",
        endpoint: "/taxonomy/allProductTypes",
      });
      const list = Array.isArray(taxonomyData) ? taxonomyData : [];
      setTaxonomies(list);
      return list;
    } catch (err) {
      console.error("Error fetching taxonomies:", err);
      setTaxonomies([]);
      setError("Failed to load taxonomies.");
      return [];
    } finally {
      setTaxonomiesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm();
    if (isAdmin) {
      Promise.all([fetchBrands(), fetchTaxonomies()]);
    }
  }, [open, isAdmin, resetForm, fetchBrands, fetchTaxonomies]);

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const setField = (field, value) => {
    setError("");
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!isAdmin) return;

    const modelNumber = form.modelNumber.trim();
    const modelName = form.modelName.trim();
    const brandId = form.brandId.trim();
    const taxonomyId = form.taxonomyId.trim();

    const missingRequired = [];
    if (!brandId) missingRequired.push("Brand");
    if (!taxonomyId) missingRequired.push("Taxonomy");
    if (!modelNumber) missingRequired.push("Model Number");
    if (!modelName) missingRequired.push("Model Name");
    if (missingRequired.length > 0) {
      setError(
        `Please complete required field${missingRequired.length > 1 ? "s" : ""}: ${missingRequired.join(", ")}.`
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        brandId,
        taxonomyId,
        modelNumber,
        modelName,
        upc: form.upc.trim(),
        imageUrl: form.imageUrl.trim(),
        hasPart: form.hasPart,
      };

      const response = await fetchWithAuth({
        method: "PUT",
        endpoint: "/model/addNewModel",
        params: { username },
        headers: { "Content-Type": "application/json" },
        data: payload,
      });

      const successMessage = messageFromApiResponse(
        response,
        "Model added successfully."
      );
      resetForm();
      onClose?.();
      onSuccess?.(successMessage, modelNumber);
    } catch (err) {
      console.error("Error adding model:", err);
      setError(
        messageFromApiError(err, "Failed to add model. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open || !isAdmin) return null;

  return (
    <div className="addNewModelModalOverlay" onClick={handleClose}>
      <div
        className="addNewModelModalContent"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-new-model-title"
      >
        <h3 id="add-new-model-title">Add New Model</h3>

        {error ? (
          <div className="addNewModelModalFormError" role="alert">
            {error}
          </div>
        ) : null}

        <div className="addNewModelModalForm">
          <div className="addNewModelModalField">
            <label htmlFor="addNewModelBrand">
              Brand{" "}
              <span className="addNewModelModalRequiredMark" aria-hidden="true">
                *
              </span>
              :
            </label>
            {brandsLoading ? (
              <div className="addNewModelModalFieldLoading">Loading brands...</div>
            ) : (
              <select
                id="addNewModelBrand"
                value={form.brandId}
                onChange={(e) => setField("brandId", e.target.value)}
                disabled={loading}
                required
                aria-required="true"
              >
                <option value="">Select Brand</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {[brand.name, brand.id].filter(Boolean).join(" - ")}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="addNewModelModalField">
            <label htmlFor="addNewModelTaxonomy">
              Taxonomy{" "}
              <span className="addNewModelModalRequiredMark" aria-hidden="true">
                *
              </span>
              :
            </label>
            {taxonomiesLoading ? (
              <div className="addNewModelModalFieldLoading">
                Loading taxonomies...
              </div>
            ) : (
              <select
                id="addNewModelTaxonomy"
                value={form.taxonomyId}
                onChange={(e) => setField("taxonomyId", e.target.value)}
                disabled={loading}
                required
                aria-required="true"
              >
                <option value="">Select Taxonomy</option>
                {taxonomies.map((taxonomy) => (
                  <option key={taxonomy.id} value={taxonomy.id}>
                    {[taxonomy.name, taxonomy.id].filter(Boolean).join(" - ")}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="addNewModelModalField">
            <label htmlFor="addNewModelNumber">
              Model Number{" "}
              <span className="addNewModelModalRequiredMark" aria-hidden="true">
                *
              </span>
              :
            </label>
            <input
              id="addNewModelNumber"
              type="text"
              value={form.modelNumber}
              onChange={(e) => setField("modelNumber", e.target.value)}
              placeholder="Enter Model Number"
              disabled={loading}
              required
              aria-required="true"
            />
          </div>

          <div className="addNewModelModalField">
            <label htmlFor="addNewModelName">
              Model Name{" "}
              <span className="addNewModelModalRequiredMark" aria-hidden="true">
                *
              </span>
              :
            </label>
            <input
              id="addNewModelName"
              type="text"
              value={form.modelName}
              onChange={(e) => setField("modelName", e.target.value)}
              placeholder="Enter Model Name"
              disabled={loading}
              required
              aria-required="true"
            />
          </div>

          <div className="addNewModelModalField">
            <label htmlFor="addNewModelUpc">UPC:</label>
            <input
              id="addNewModelUpc"
              type="text"
              value={form.upc}
              onChange={(e) => setField("upc", e.target.value)}
              placeholder="Enter UPC"
              disabled={loading}
            />
          </div>

          <div className="addNewModelModalField">
            <label htmlFor="addNewModelImageUrl">Image URL:</label>
            <input
              id="addNewModelImageUrl"
              type="text"
              value={form.imageUrl}
              onChange={(e) => setField("imageUrl", e.target.value)}
              placeholder="Enter image URL"
              disabled={loading}
            />
          </div>

          <label className="addNewModelModalCheckboxRow" htmlFor="addNewModelHasPart">
            <input
              id="addNewModelHasPart"
              type="checkbox"
              checked={form.hasPart}
              onChange={(e) => setField("hasPart", e.target.checked)}
              disabled={loading}
            />
            Has part
          </label>
        </div>

        <div className="addNewModelModalActions">
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || brandsLoading || taxonomiesLoading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
          <button type="button" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Success toast used after Add New Model completes. */
export function AddNewModelSuccessPopup({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className="addNewModelSuccessPopup" role="status" aria-live="polite">
      <div className="addNewModelSuccessMessage">{message}</div>
      <button
        type="button"
        className="addNewModelSuccessPopupClose"
        onClick={onDismiss}
        aria-label="Dismiss success message"
      >
        &times;
      </button>
    </div>
  );
}
